/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'

import {KTSVG} from '../../../helpers'
import {KTCard, KTCardBody, Select, Loading, Datepicker} from '../../../components'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {overtimeCreate} from '../../../redux/slices/staff.slice'
import {staffGetAll, basicLookup} from '../../../redux/slices/lookup.slice'
import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs,
} from '../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  staffLookup: state.lookup.staffLookup,
  OvertimeType: state.lookup.OvertimeType,
  loading: state.lookup.loading,
})

const selectStaffData = (state) => ({
  isRedirect: state.staff.isRedirect,
  loading: state.staff.loading,
})

function Create() {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  const pageUrl = useMemoizedPageUrl(location)

  const {staffLookup, OvertimeType, loading} = useSelector(selectLookupData)
  const {isRedirect, loading: createLoading} = useSelector(selectStaffData)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(staffGetAll())
    dispatch(basicLookup('OvertimeType'))
  }, [dispatch])

  const validSchema = useMemoizedValidationSchema(() => Yup.object().shape({
    StaffId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    StartDate: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    HourDuration: Yup.number()
      .typeError(intl.formatMessage({id: 'REQUIRED.VALID'}))
      .required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    OvertimeTypeId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
  }), [intl])

  const formValues = useMemoizedFormValues({
    StaffId: '',
    StartDate: new Date(),
    HourDuration: 0,
    OvertimeTypeId: '',
    Name: '',
  })

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: async (values, {setSubmitting, resetForm}) => {
      setSubmitting(true)
      dispatch(overtimeCreate(values))
      resetForm()
    },
  })

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.OVERTIME'}),
      path: '/staff/overtime',
      isSeparator: false,
      isActive: false,
    },
  ], [intl])

  useEffect(() => {
    if (isRedirect && isRedirect.id) navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${isRedirect.id}`)
  }, [isRedirect, navigate, pageUrl])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>{intl.formatMessage({id: 'BC.OVERTIME.ADD'})}</PageTitle>
      <KTCard>
        <KTCardBody>
          <form className='form' onSubmit={formik.handleSubmit} noValidate>
            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'STAFF_NAME'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={staffLookup}
                  required
                  {...formik.getFieldProps('StaffId')}
                  onChange={(item) => formik.setFieldValue('StaffId', Number(item && item.value))}
                />
                {formik.touched.StaffId && formik.errors.StaffId && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.StaffId}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'EXPLANATION'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <textarea
                  type='text'
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  rows={4}
                  {...formik.getFieldProps('Name')}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 required fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'OVERTIME.DATE'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Datepicker
                  required
                  {...formik.getFieldProps('StartDate')}
                  onChange={([date]) => formik.setFieldValue('StartDate', date)}
                />
                {formik.touched.StartDate && formik.errors.StartDate && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.StartDate}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 required fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'OVERTIME.TIME'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='number'
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('HourDuration')}
                  onFocus={(event) => event.target.select()}
                />
                {formik.touched.HourDuration && formik.errors.HourDuration && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.HourDuration}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'OVERTIME.TYPE'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={OvertimeType}
                  required
                  {...formik.getFieldProps('OvertimeTypeId')}
                  onChange={(item) => formik.setFieldValue('OvertimeTypeId', Number(item && item.value))}
                />
                {formik.touched.OvertimeTypeId && formik.errors.OvertimeTypeId && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.OvertimeTypeId}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row pt-15'>
              <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                <button
                  className='btn btn-light'
                  onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}`)}
                >
                  {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                </button>
                <button
                  type='submit'
                  className='btn btn-primary'
                  data-kt-users-modal-action='submit'
                  disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                >
                  <span className='indicator-label'>
                    {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                  </span>
                  {formik.isSubmitting && (
                    <span className='indicator-progress'>
                      {intl.formatMessage({id: 'PLEASE'})}
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>
            </div>
          </form>
        </KTCardBody>
      </KTCard>

      {(loading | createLoading) > 0 && <Loading />}
    </>
  )
}
export {Create}
